/**
 * Integration Service - Serviço para gerenciamento de integrações (pixels, webhooks, etc)
 *
 * Fornece métodos para buscar, criar, atualizar e deletar integrações
 */

import { api } from './api';
import { toApiError } from './dynamicFlowsService';
import type { PixelHealth, PixelPlatform } from '@/types/pixel';

export type IntegrationType = 'pixel' | 'leads';
export type IntegrationStatus = 'active' | 'inactive';

export interface Company {
  id: string;
  name: string;
}

export interface Integration {
  id: string;
  endpoint_url: string;
  api_key?: string | null;
  id_plan?: string | null;
  type: IntegrationType;
  id_company: string;
  id_user: string;
  id_video: string | null;
  id_flow?: string | null;
  covered_video_ids?: string[];
  health?: PixelHealth;
  createdAt?: string;
  is_dynamic_vsl: boolean;
  id_funil?: string | null;
  event_source?: string | null; // Eventos enviados para o pixel
  machine_code?: number | null;
  email_sequence_code?: number | null;
  sequence_level_code?: number | null;
  status: IntegrationStatus;
  created_at: string;
  updated_at: string;
  company?: Company;
}

export interface CreateIntegrationData {
  endpoint_url: string;
  type: IntegrationType;
  id_user: string;
  id_video: string;
  api_key?: string;
  status?: IntegrationStatus;
  id_company?: string;
  id_plan?: string;
  /** Plataforma do pixel (allowlist da API: facebook | google-ads | tiktok). Transporte para resolver id_company. */
  platform?: string;
  event_source?: string;
  machine_code?: string;
  email_sequence_code?: string;
  sequence_level_code?: string;
}

export interface UpdateIntegrationData {
  endpoint_url?: string;
  status?: IntegrationStatus;
  api_key?: string;
}

export interface PixelHealthRow {
  id_video: string;
  platform: string;
  first_at?: string;
  last_at?: string;
  last_event?: string;
  total: number;
  last24h: number;
}

export interface CreateFlowPixelData {
  endpoint_url: string;
  api_key: string;
  id_plan: string;
  platform: PixelPlatform;
  status?: IntegrationStatus;
}

async function flowPixelRequest<T>(request: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await request()).data;
  } catch (error) {
    throw toApiError(error);
  }
}

const flowPixelsPath = (flowId: string) => `/dynamic-flows/${encodeURIComponent(flowId)}/pixels`;

export const integrationService = {
  getFlowPixels: (flowId: string) =>
    flowPixelRequest<Integration[]>(() => api.get(flowPixelsPath(flowId))),
  createFlowPixel: (flowId: string, data: CreateFlowPixelData) =>
    flowPixelRequest<Integration>(() => api.post(flowPixelsPath(flowId), data)),
  updateFlowPixel: (flowId: string, pixelId: string, data: UpdateIntegrationData) =>
    flowPixelRequest<Integration>(() => api.patch(`${flowPixelsPath(flowId)}/${encodeURIComponent(pixelId)}`, data)),
  deleteFlowPixel: (flowId: string, pixelId: string) =>
    flowPixelRequest<{ success: boolean }>(() => api.delete(`${flowPixelsPath(flowId)}/${encodeURIComponent(pixelId)}`)),

  /**
   * GET /integrations/pixel-health?userId=xxx
   * Evidência de disparo de pixel por vídeo+plataforma (beacon do player / dispatch FB)
   */
  getPixelHealth: async (userId: string): Promise<PixelHealthRow[]> => {
    try {
      const response = await api.get<{ response: PixelHealthRow[] }>(
        `/integrations/pixel-health?userId=${encodeURIComponent(userId)}`
      );
      return response?.response || [];
    } catch {
      return [];
    }
  },

  /**
   * GET /companies
   * Busca todas as empresas/plataformas de integração disponíveis
   */
  getAllCompanies: async (): Promise<Company[]> => {
    return await api.get<Company[]>('/companies');
  },

  /**
   * GET /integrations?userId=xxx&type=pixel
   * Busca integrações de um usuário por tipo
   */
  getByUserIdAndType: async (
    userId: string,
    type: IntegrationType,
    dynamicVsl?: boolean
  ): Promise<Integration[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append('userId', userId);
    queryParams.append('type', type);

    if (dynamicVsl !== undefined) {
      queryParams.append('dynamicVsl', dynamicVsl.toString());
    }

    const response = await api.get<Integration[]>(
      `/integrations?${queryParams.toString()}`
    );

    return response;
  },

  /**
   * GET /integrations
   * Busca todas as integrações do usuário
   */
  getAll: async (userId: string, type?: IntegrationType): Promise<Integration[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append('userId', userId);
    if (type) {
      queryParams.append('type', type);
    }

    return await api.get<Integration[]>(
      `/integrations?${queryParams.toString()}`
    );
  },

  /**
   * GET /integrations/:id
   * Busca uma integração específica por ID
   */
  getById: async (integrationId: string): Promise<Integration> => {
    return await api.get(`/integrations/${integrationId}`);
  },

  /**
   * POST /integrations
   * Cria uma nova integração
   */
  create: async (data: CreateIntegrationData): Promise<Integration> => {
    return await api.post('/integrations', data);
  },

  /**
   * PATCH /integrations/:id
   * Atualiza uma integração
   */
  update: async (
    integrationId: string,
    data: UpdateIntegrationData
  ): Promise<Integration> => {
    return await api.patch(`/integrations/${integrationId}`, data);
  },

  /**
   * DELETE /integrations/:id
   * Deleta uma integração
   */
  delete: async (integrationId: string): Promise<{ success: boolean; message: string }> => {
    return await api.delete(`/integrations/${integrationId}`);
  },
};

export default integrationService;
